/**
 * @module profile/components/portfolio/portfolio_gallery
 */
import React, { PropTypes, Component } from 'react';
import update from 'immutability-helper';
import chunk from 'lodash/chunk';
import { v4 } from 'node-uuid';
import { DragDropContext } from 'react-dnd';
import HTML5Backend from 'react-dnd-html5-backend';
import { PAGINATION_PAGE_SIZE } from 'common/variables';
import GalleryNoResults from 'common/components/gallery_no_results';
import ConfirmationDialog from 'common/components/modal/confirmation_dialog';
import PortfolioGalleryTile from 'profile/components/portfolio/portfolio_gallery_tile';

/**
 * Portfolio gallery component
 * @author Kuba Siemiątkowski <kuba@crowdspring.com>
 */
class PortfolioGallery extends Component {
  constructor(props) {
    super(props);
    this.state = {
      items: [],
      confirmationDialogVisible: false,
      entryToRemove: null,
    };

    this.moveItem = this.moveItem.bind(this);
    this.removeEntry = this.removeEntry.bind(this);
    this.toggleConfirmationDialog = this.toggleConfirmationDialog.bind(this);
    this.performEntryDelete = this.performEntryDelete.bind(this);
  }

  onComponentDidMount() {
    this.setState({
      items: this.props.items,
    });
  }

  toggleConfirmationDialog() {
    const newState = {
      confirmationDialogVisible: !this.state.confirmationDialogVisible,
    };

    if (!newState.confirmationDialogVisible) {
      newState.entryToRemove = null;
    }

    this.setState({ ...newState });
  }

  /**
   * This is called when a gallery tile is clicked, to show the delete confirmation
   * modal and save the id of entry we're deleting.
   */
  removeEntry(entryId) {
    this.setState({ entryToRemove: entryId });
    this.toggleConfirmationDialog();
  }

  /**
   * This function actually removes the entry.
   */
  performEntryDelete() {
    this.props.onDeleteEntry(this.props.profileUsername, this.state.entryToRemove)
      .then(() => {
        this.toggleConfirmationDialog();
      });
  }

  /**
   * When we're reordering items, we want to have a copy of visibleEntries locally so
   * that we can modify it, and save in the store to keep track of the dirty/clean state.
   * When item is dropped we exchange entries with their places and save the new state.
   */
  moveItem(dragIndex, dropIndex) {
    const { onSetPortfolioDirty, editMode, reorderedEntries } = this.props;
    let items = [...this.state.items];

    if ((editMode && !reorderedEntries) || !items.length) {
      items = [...this.props.items];
    }
    const dragItem = {
      ...items[dragIndex],
      sort_order: dropIndex,
    };
    const dropTargetItem = {
      ...items[dropIndex],
      sort_order: dragIndex
    };
    let newState = { items };

    newState = (update(newState, {
      items: {
        $splice: [
          [dropIndex, 1, dropTargetItem],
          [dragIndex, 1],
          [dropIndex, 0, dragItem],
        ],
      },
    }));

    this.setState(newState);

    onSetPortfolioDirty([...newState.items]);
  }

  renderSingleItem(item, i) {
    const { userAccess, onToggleEntryDetails, editMode } = this.props;

    return (
      <PortfolioGalleryTile
        editMode={editMode}
        key={item.id}
        userAccess={userAccess}
        item={item}
        index={i}
        onToggleEntryDetails={onToggleEntryDetails}
        onDeleteEntry={this.removeEntry}
        moveItem={this.moveItem}
      />
    );
  }

  renderGalleryPage(items, iteratorBoost) {
    const increaseIterator = iteratorBoost || 0;

    return (
      <div key={v4()} className="gallery-items row small-up-1 medium-up-2 large-up-3">
        { items.map((item, i) => this.renderSingleItem(item, i + increaseIterator)) }
      </div>
    );
  }

  renderGalleryItems(items) {
    const { editMode } = this.props;

    if (editMode) {
      const elements = [];
      const chunkedItems = chunk(items, PAGINATION_PAGE_SIZE);

      for (let j = 0; j < chunkedItems.length; j += 1) {
        if (j > 0) {
          elements.push(
            <div key={v4()} className="row gallery-page-separator">
              <div className="small-16 column">
                <span className="page-indicator">Page {j + 1}</span>
              </div>
            </div>
          );
        }
        elements.push(
          this.renderGalleryPage(chunkedItems[j], PAGINATION_PAGE_SIZE * j)
        );
      }

      return elements;
    }
    return this.renderGalleryPage(items);
  }

  render() {
    const { items, editMode, reorderedEntries } = this.props;
    let renderItems;

    // if page is not in edit mode or there are no reordered items yet, we know that
    // user just started editing the list
    if (!editMode || (editMode && !reorderedEntries) || !this.state.items.length) {
      renderItems = items;
    } else {
      renderItems = this.state.items;
    }

    if (!renderItems.length) {
      return (
        <div className="gallery-page">
          <div className="gallery-items row">
            <div className="columns">
              <GalleryNoResults />
            </div>
          </div>
        </div>
      );
    }

    const dialogMessage = 'Are you sure you want to remove this item ?';

    return (
      <div className="gallery-page">
        { this.renderGalleryItems(renderItems) }
        <ConfirmationDialog
          dialogMessage={dialogMessage}
          dialogVisible={this.state.confirmationDialogVisible}
          toggleDialog={this.toggleConfirmationDialog}
          submitAction={this.performEntryDelete}
          dialogHeader="Delete Entry"
        />
      </div>
    );
  }
}

/**
 * PropTypes.
 *
 * @property {string} profileUsername - username of logged in user
 * @property {array} items - array of portfolio items to display
 * @property {bool} editMode - boolean value defining if portfolio page is in edit mode
 * @property {object} reorderedEntries - object with new entries ordering
 * @property {object} userAccess - logged user's privileges
 * @property {func} onToggleEntryDetails - toggle portfolio item details
 * @property {func} onSetPortfolioDirty - function called when portfolio ordering changes
 * @property {func} onDeleteEntry - function to call when deleting portfolio
 */
PortfolioGallery.propTypes = {
  profileUsername: PropTypes.string,
  items: PropTypes.array,
  editMode: PropTypes.bool,
  reorderedEntries: PropTypes.object,
  userAccess: PropTypes.object,
  onToggleEntryDetails: PropTypes.func,
  onSetPortfolioDirty: PropTypes.func,
  onDeleteEntry: PropTypes.func,
};

export default DragDropContext(HTML5Backend)(PortfolioGallery);



// WEBPACK FOOTER //
// ./profile/components/portfolio/portfolio_gallery.jsx